import { BCLayoutCreator } from '@bimser/components';
import * as LayoutCreatorEntities from '@bimser/components/dist/BCLayoutCreator/common/entities';
import { FormItemTypes, IDictionary, isNullOrUndefined, MLHelper, SizeType } from '@bimser/core';
import IconBreadcrumbArrow from '@bimser/icons/16/breadcrumps-arrow';
import _isEqual from 'lodash/isEqual';
import {
	CovertFontToCSS,
	HeaderMode,
	ICSSProperties,
	IFont,
	IPropertyField
} from '../../../entities/common';
import Styles from '../assets/panelBaseStyles.scss';
import { IPanelBaseProperties } from '../entities/IPanelBaseProperties';
import ContainerControl from './ContainerControl';
import { JSX } from 'react';
import classNames from 'classnames/bind';
const cx = classNames.bind(Styles);

export default class PanelBase extends ContainerControl {
	headerMode: HeaderMode;
	collapsedDefault: boolean;
	titleFont: IFont;
	paddingType: SizeType;

	constructor() {
		super();

		this.titleFont = {
			bold: true,
			color: '#777',
			family: 'Source Sans Pro, sans-serif',
			italic: false,
			size: '14',
			strikethrough: false,
			underline: false
		};
		this.paddingType = 'middle';

		this.onCollapsedClick = this.onCollapsedClick.bind(this);
	}

	__loadProperties(data: IPanelBaseProperties) {
		super.__loadProperties(data);

		this.__proxyChanged.headerMode = data.headerMode;
		this.__proxyChanged.collapsedDefault = data.collapsedDefault;
		this.__proxyChanged.titleFont = data.titleFont;
	}

	private onCollapsedClick() {
		this.__proxyChanging.collapsedDefault = !this.collapsedDefault;
	}

	private renderHeader() {
		if (this.headerMode !== 'none') {
			const headerClassNames = cx({
				panelHeader: true,
				collapsible: this.headerMode === 'collapsible'
			});

			const headerCollapseButtonClassNames = cx({
				collapseBtn: true,
				collapsed: !this.collapsedDefault
			});

			const headerStyles: ICSSProperties = { ...CovertFontToCSS(this.titleFont) };

			return (
				<div className={Styles.panelHeaderContainer}>
					<div
						className={headerClassNames}
						onClick={this.onCollapsedClick}
						style={headerStyles}
					>
						<span>{MLHelper.getMLText(this.title)}</span>
						{this.headerMode === 'collapsible' ? (
							<span className={headerCollapseButtonClassNames}>
								<IconBreadcrumbArrow
									width="16px"
									height="16px"
								/>
							</span>
						) : null}
					</div>
				</div>
			);
		}
	}

	__getPropertyFields(): IPropertyField[] {
		const fields = super.__getPropertyFields();
		return [
			...fields,
			{
				name: 'headerMode',
				type: 'enum',
				enumMembers: ['none', 'titled', 'collapsible'] as HeaderMode[]
			},
			{
				name: 'collapsedDefault',
				type: 'boolean'
			}
		];
	}

	__getComponentData(): IDictionary<any> {
		return {
			...super.__getComponentData(),
			headerMode: this.headerMode,
			collapsedDefault: this.collapsedDefault,
			titleFont: this.titleFont
		};
	}

	__getPanelContainerStyles(): ICSSProperties {
		return this.style;
	}

	__parentItem: LayoutCreatorEntities.LayoutStructureItem;
	__getLayoutParentItem(): LayoutCreatorEntities.LayoutStructureItem {
		const newParentItem = LayoutCreatorEntities.LayoutStructureItemFactory({
			id: this.name,
			type: this.controlType,
			items: this.__childrenItem.result
		});

		if (this.__parentItem?.equals(newParentItem)) {
			return this.__parentItem;
		} else {
			return newParentItem;
		}
	}

	__render(children?: JSX.Element) {
		const panelContentContainerClassNames = cx({
			panelContentContainer: true,
			collapsed: this.headerMode === 'collapsible' ? this.collapsedDefault : false
		});

		let isScrollbar = true;
		let stylePanel: ICSSProperties;
		if (this.controlType == FormItemTypes.EControlTypes.Panel) {
			stylePanel = { backgroundColor: this.style.backgroundColor };
			isScrollbar = !isNullOrUndefined(this.style.height);
		} else if (
			this.controlType == FormItemTypes.EControlTypes.Tab ||
			this.controlType == FormItemTypes.EControlTypes.InputGroup
		) {
			isScrollbar = false;
		}

		return super.__render(
			<div className={Styles.panelContainer}>
				{this.renderHeader()}
				<div
					className={panelContentContainerClassNames}
					style={stylePanel}
				>
					<BCLayoutCreator.Viewer
						containerClassName={`CSP_form_${this.__form.projectName}_${this.__form.formName}`}
						view={this.prepareRenderOrderProperties()}
						controlRenderManager={this.__form.__controlRenderManager}
						isControlVisible={this.__form.__isControlVisible}
						parentItem={this.__getLayoutParentItem()}
						paddingType={this.paddingType}
						containerStyles={this.__getPanelContainerStyles()}
						windowSize={{ width: this.__form.windowSize?.width || 1000 }}
						hasScrollbar={isScrollbar}
						isSubLayout
					/>
				</div>
			</div>
		);
	}
}
